import React, { useId } from 'react';

/**
 * Flat dot grid drawn as an inline SVG pattern in `currentColor`, so it takes any token text
 * colour (`text-on-navy/15`, `text-line/40`). Fills its positioned parent.
 */
export function DotPattern({ className = '', gap = 18, radius = 1.1 }) {
  const id = `dots-${useId().replace(/:/g, '')}`;
  return (
    <svg aria-hidden="true" className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}>
      <defs>
        <pattern id={id} width={gap} height={gap} patternUnits="userSpaceOnUse">
          <circle cx={gap / 2} cy={gap / 2} r={radius} fill="currentColor" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/**
 * Flat poster background for a section (use inside `relative overflow-hidden`).
 * `variant="navy"` paints a solid navy block; `"light"` a solid surface. On top: an optional dot grid
 * (`grid`) and a few solid, slowly bobbing geometric shapes in brand tokens (`shapes`), kept to the
 * right-hand side so text contrast is never affected. Solid colours only: every fill is a token.
 */
export default function PosterBackdrop({ variant = 'navy', grid = true, shapes = true, className = '' }) {
  const navy = variant === 'navy';
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${navy ? 'bg-navy-deep' : 'bg-surface'} ${className}`}>
      {grid && <DotPattern className={navy ? 'text-on-navy/[0.09]' : 'text-line/30'} />}
      {shapes && (
        <>
          <div className={`absolute -right-24 -top-28 h-72 w-72 animate-bob-slow rounded-full sm:h-96 sm:w-96 ${navy ? 'bg-electric/25' : 'bg-sky-soft'}`} />
          <div className={`absolute -bottom-10 right-[18%] hidden h-24 w-24 animate-bob rotate-12 rounded-3xl sm:block ${navy ? 'bg-pop/20' : 'bg-pop-soft'}`} />
          <div className={`absolute right-[8%] top-[58%] hidden h-3 w-28 rounded-full md:block ${navy ? 'bg-sun/40' : 'bg-sun-soft'}`} />
        </>
      )}
    </div>
  );
}
